// hooks/useOnboardingWizard.js
//
// Single source of truth for the progressive-onboarding form. Loads the merchant's saved
// onboarding record, hydrates an editable form, validates + persists each step to the existing
// POCAS endpoints, and refetches the backend progress/gate model after every write.
//
// This is intentionally decoupled from presentation so the SAME logic drives both the full-page
// guided wizard (Onboarding Hub) and the overlay wizard shown at the order submit-gate.
//
// Save strategy (unchanged from the original checklist): POCAS `/update` overwrites whole
// sub-documents (basicDetails/addressDetails/bankDetails), so every save sends the FULL merged
// object (preloaded record + edits) to avoid clobbering fields the user didn't touch.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getOnboardingRecord,
  updateOnboardingDetails,
  uploadDocument,
  submitOnboarding,
} from '../services/onboardingService';
import useOnboardingProgress from './useOnboardingProgress';
import { friendlyApiError } from '../utils/apiError';

// ── helpers ─────────────────────────────────────────────────────────────────
const PLACEHOLDER = '********';
const clean = (v) => {
  const s = (v ?? '').toString().trim();
  return s === PLACEHOLDER ? '' : s;
};
// Null-safe read (backend responses are snake_case; POCAS/OMS map internally).
const pick = (obj, key) => (obj && obj[key] !== undefined && obj[key] !== null ? obj[key] : undefined);

export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/i;
export const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/i;
export const PINCODE_REGEX = /^\d{6}$/;
export const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
export const MOBILE_REGEX = /^[6-9]\d{9}$/;

// KYC document tiles. Aadhaar is optional and split into front/back — the backend accepts
// AADHAAR_FRONT / AADHAAR_BACK (or a single AADHAAR). Any one document satisfies the step.
export const DOC_TILES = [
  { type: 'PAN', label: 'PAN card', hint: 'Recommended' },
  { type: 'AADHAAR_FRONT', label: 'Aadhaar (front)', hint: 'Optional' },
  { type: 'AADHAAR_BACK', label: 'Aadhaar (back)', hint: 'Optional' },
  { type: 'GST', label: 'GST certificate' },
  { type: 'CHEQUE', label: 'Cancelled cheque' },
];

const EMPTY_FORM = {
  name: '', email: '', mobile: '', brandName: '',
  line1: '', line2: '', city: '', state: '', pincode: '',
  pan: '', gst: '', aadhaar: '',
  accNo: '', accHolder: '', ifsc: '', bankName: '', branch: '',
};

/**
 * @returns everything the wizard UI needs: identity, progress/gate, editable form, per-step
 * validators/savers (each resolves to a boolean success), document upload + submit actions.
 */
export default function useOnboardingWizard() {
  const merchantId = useSelector((s) => s.auth?.merchantId) || localStorage.getItem('merchantId') || null;
  const parentClientId = useSelector((s) => s.auth?.parentClientId) || localStorage.getItem('parentClientId') || null;
  const token = useSelector((s) => s.auth?.token) || localStorage.getItem('authToken') || null;

  const {
    phases, status, completionPercent, businessOpsUnlocked,
    agreementRequired, agreementSigned, bankVerified, refetch,
  } = useOnboardingProgress();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [walletType, setWalletType] = useState('PREPAID');
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);

  const [saving, setSaving] = useState(null); // section id currently saving
  const [uploading, setUploading] = useState(null); // doc type uploading
  const [sectionError, setSectionError] = useState({});
  const [sectionErrorRef, setSectionErrorRef] = useState({}); // support/correlation ids per section
  const [sectionMsg, setSectionMsg] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const phaseComplete = useMemo(() => {
    const m = {};
    (phases || []).forEach((p) => { m[p.phase] = !!p.complete; });
    return m;
  }, [phases]);

  const hydrate = useCallback((rec) => {
    const b = pick(rec, 'basic_details') || {};
    const addrRoot = pick(rec, 'address_details') || {};
    const addr = pick(addrRoot, 'communication_address') || {};
    const bank = pick(rec, 'bank_details') || {};
    const wallet = pick(rec, 'wallet_preferences') || {};
    const docs = pick(rec, 'documents') || [];

    setForm({
      name: clean(pick(b, 'name')),
      email: clean(pick(b, 'email')),
      mobile: clean(pick(b, 'phone')),
      brandName: clean(pick(b, 'brand_name')),
      line1: clean(pick(addr, 'line1')),
      line2: clean(pick(addr, 'line2')),
      city: clean(pick(addr, 'city')),
      state: clean(pick(addr, 'state')),
      pincode: clean(pick(addr, 'pincode')),
      pan: clean(pick(b, 'pan_number')),
      gst: clean(pick(b, 'gst_number')),
      aadhaar: clean(pick(b, 'aadhaar_number')),
      accNo: clean(pick(bank, 'account_number')),
      accHolder: clean(pick(bank, 'account_holder_name')),
      ifsc: clean(pick(bank, 'ifsc_code')),
      bankName: clean(pick(bank, 'bank_name')),
      branch: clean(pick(bank, 'branch_name')),
    });
    setWalletType(clean(pick(wallet, 'preferred_type')) || 'PREPAID');
    setUploadedDocs(
      (Array.isArray(docs) ? docs : [])
        .map((d) => (pick(d, 'document_type') || '').toString().toUpperCase())
        .filter(Boolean)
    );
  }, []);

  const load = useCallback(async () => {
    if (!parentClientId) { setLoading(false); return; }
    setLoading(true);
    setLoadError(null);
    try {
      const rec = await getOnboardingRecord(parentClientId, token);
      hydrate(rec || {});
    } catch (err) {
      setLoadError(friendlyApiError(err, { action: 'load your saved details', fallback: 'Could not load your saved details.' }).message);
    } finally {
      setLoading(false);
    }
  }, [parentClientId, token, hydrate]);

  useEffect(() => { load(); }, [load]);

  const basicDetailsPayload = () => ({
    name: form.name.trim(),
    email: form.email.trim(),
    phone: form.mobile.trim(),
    brand_name: form.brandName.trim(),
    pan_number: form.pan.trim(),
    gst_number: form.gst.trim(),
    aadhaar_number: form.aadhaar.trim(),
  });

  const runSave = async (id, validateFn, payloadFn, successMsg) => {
    const err = validateFn?.();
    if (err) { setSectionError((e) => ({ ...e, [id]: err })); return false; }
    setSectionError((e) => ({ ...e, [id]: null }));
    setSectionErrorRef((r) => ({ ...r, [id]: null }));
    setSectionMsg((m) => ({ ...m, [id]: null }));
    setSaving(id);
    try {
      await updateOnboardingDetails({
        parent_client_id: parentClientId,
        merchant_id: merchantId,
        wallet_type_preference: walletType || 'PREPAID',
        ...payloadFn(),
      }, token);
      setSectionMsg((m) => ({ ...m, [id]: successMsg }));
      if (refetch) await refetch();
      return true;
    } catch (e) {
      const { message, correlationId } = friendlyApiError(e, { action: 'save your details', fallback: 'Could not save. Please try again.' });
      setSectionError((prev) => ({ ...prev, [id]: message }));
      setSectionErrorRef((prev) => ({ ...prev, [id]: correlationId }));
      return false;
    } finally {
      setSaving(null);
    }
  };

  const saveProfile = () => runSave(
    'PROFILE',
    () => {
      if (!form.line1.trim() || !form.city.trim() || !form.state.trim()) return 'Address line 1, city and state are required.';
      if (!PINCODE_REGEX.test(form.pincode.trim())) return 'Enter a valid 6-digit pincode.';
      return null;
    },
    () => ({
      basic_details: basicDetailsPayload(),
      address_details: {
        communication_address: {
          line1: form.line1.trim(), line2: form.line2.trim(),
          city: form.city.trim(), state: form.state.trim(), country: 'India', pincode: form.pincode.trim(),
        },
      },
    }),
    'Profile & address saved.',
  );

  const saveBusiness = () => runSave(
    'BUSINESS',
    () => {
      const anyId = form.pan.trim() || form.gst.trim() || form.aadhaar.trim();
      if (!anyId) return 'Provide at least one of PAN, GST or Aadhaar.';
      if (form.pan.trim() && !PAN_REGEX.test(form.pan.trim())) return 'Enter a valid PAN (e.g., ABCDE1234F).';
      return null;
    },
    () => ({ basic_details: basicDetailsPayload() }),
    'Business details saved.',
  );

  const saveBank = () => runSave(
    'BANK',
    () => {
      if (!form.accNo.trim() || !form.ifsc.trim()) return 'Account number and IFSC are required.';
      if (!IFSC_REGEX.test(form.ifsc.trim())) return 'Enter a valid IFSC code.';
      return null;
    },
    () => ({
      bank_details: {
        account_number: form.accNo.trim(), account_holder_name: form.accHolder.trim(),
        ifsc_code: form.ifsc.trim().toUpperCase(), bank_name: form.bankName.trim(), branch_name: form.branch.trim(),
      },
    }),
    'Bank details saved.',
  );

  const onUpload = async (docType, file) => {
    if (!file || !merchantId) return false;
    const docLabel = (DOC_TILES.find((d) => d.type === docType)?.label || 'document').toLowerCase();
    setUploading(docType);
    setSectionError((e) => ({ ...e, DOCUMENTS: null }));
    setSectionErrorRef((r) => ({ ...r, DOCUMENTS: null }));
    try {
      await uploadDocument(merchantId, docType, file, token);
      setUploadedDocs((d) => Array.from(new Set([...d, docType.toUpperCase()])));
      if (refetch) await refetch();
      return true;
    } catch (e) {
      const { message, correlationId } = friendlyApiError(e, { action: `upload your ${docLabel}`, fallback: `Could not upload your ${docLabel}.` });
      setSectionError((prev) => ({ ...prev, DOCUMENTS: message }));
      setSectionErrorRef((prev) => ({ ...prev, DOCUMENTS: correlationId }));
      return false;
    } finally {
      setUploading(null);
    }
  };

  const requiredDone = !!(phaseComplete.PROFILE && phaseComplete.BUSINESS && phaseComplete.DOCUMENTS);

  const submit = async () => {
    if (!requiredDone) return false;
    setSubmitting(true);
    setSubmitError(null);
    setSectionErrorRef((r) => ({ ...r, REVIEW: null }));
    try {
      await submitOnboarding(merchantId, parentClientId, token);
      if (refetch) await refetch();
      return true;
    } catch (e) {
      const { message, correlationId } = friendlyApiError(e, { action: 'submit for verification', fallback: 'Could not submit for review. Please try again.' });
      setSubmitError(message);
      setSectionErrorRef((prev) => ({ ...prev, REVIEW: correlationId }));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const missingFor = useCallback((key) => {
    const p = (phases || []).find((x) => x.phase === key);
    return p && !p.complete && Array.isArray(p.missing) && p.missing.length ? p.missing : [];
  }, [phases]);

  return {
    // identity + gate/progress
    merchantId, parentClientId, token,
    status, completionPercent, businessOpsUnlocked,
    agreementRequired, agreementSigned, bankVerified,
    phases, phaseComplete, requiredDone, missingFor, refetch,
    // form
    loading, loadError, reload: load,
    form, set, setForm,
    walletType, setWalletType,
    uploadedDocs,
    // per-section status
    saving, uploading, sectionError, sectionErrorRef, sectionMsg,
    submitting, submitError,
    // actions (each resolves to a boolean success)
    saveProfile, saveBusiness, saveBank, onUpload, submit,
  };
}
